import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SharedInput   from '../SharedInput';
import SharedButton  from '../SharedButton';
import SharedCard    from '../SharedCard';
import ImageUploader from '../ImageUploader';

const UserManagement = ({ currentUser }) => {
  const isSuperAdmin = currentUser?.username === 'admin';
  if (!isSuperAdmin) {
    return (
      <SharedCard title="Gestión de Usuarios">
        <p className="p-4 text-center text-red-600">No autorizado</p>
      </SharedCard>
    );
  }

  const [users, setUsers]               = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [newUser, setNewUser]           = useState({ username: '', password: '', allowedPages: [] });
  const [academyName, setAcademyName]   = useState('');
  const [academyLogo, setAcademyLogo]   = useState('');
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');

  // FIX: StudentRegistration eliminado — ahora Estudiantes cubre registro y lista
  const availablePages = [
    { value: 'DashboardPanel',    label: 'Dashboard'      },
    { value: 'StudentsList',      label: 'Estudiantes'    }, // incluye registro
    { value: 'POS',               label: 'Punto de Venta' },
    { value: 'ProductManagement', label: 'Productos'      },
    { value: 'FinancesPanel',     label: 'Finanzas'       },
    { value: 'Calendar',          label: 'Calendario'     },
    { value: 'TeacherManagement', label: 'Maestros'       },
    { value: 'AttendancePanel',   label: 'Asistencia'     },
    { value: 'PaymentsPanel',     label: 'Pagos'          },
    { value: 'SalesPage',         label: 'Ventas'         },
    { value: 'FestivalesPanel',   label: 'FestivalesPanel'},
  ];

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/api/admin/list_users.php', { withCredentials: true });
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error al cargar usuarios', err);
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  const handlePageCheckboxChange = e => {
    const { value, checked } = e.target;
    setNewUser(prev => ({
      ...prev,
      allowedPages: checked
        ? [...prev.allowedPages, value]
        : prev.allowedPages.filter(p => p !== value)
    }));
  };

  const handleAddOrUpdateUser = async e => {
    e.preventDefault();
    if (!newUser.username || !newUser.password) {
      setError('Usuario y contraseña son obligatorios');
      return;
    }

    const formData = new FormData();
    formData.append('username',     newUser.username);
    formData.append('password',     newUser.password);
    formData.append('role',         'admin');
    formData.append('pages',        JSON.stringify(newUser.allowedPages));
    formData.append('academy_name', academyName);
    if (academyLogo instanceof File) formData.append('logo', academyLogo);
    else formData.append('logo_url', academyLogo);
    if (editingUserId) formData.append('id', editingUserId);

    try {
      const endpoint = editingUserId ? '/api/admin/update_user.php' : '/api/admin/create_user.php';
      const res = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      if (res.data.success) {
        setSuccess(editingUserId ? 'Usuario actualizado.' : 'Usuario creado.');
        setError('');
        resetForm();
        fetchUsers();
      } else {
        setError(res.data.message || 'Error al guardar');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error de conexión');
    }
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleEditUser = user => {
    setEditingUserId(user.id);
    setNewUser({ username: user.username, password: '', allowedPages: user.pages || [] });
    setAcademyName(user.academy_name || '');
    setAcademyLogo(user.academy_logo || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteUser = async userId => {
    const u = users.find(u => u.id === userId);
    if (u?.username === 'admin') { alert('El super-admin no puede eliminarse.'); return; }
    if (!window.confirm('¿Eliminar este usuario y todos sus datos?')) return;
    try {
      const res = await axios.post('/api/admin/delete_user.php', { id: userId }, { withCredentials: true });
      if (res.data.success) fetchUsers();
      else alert(res.data.message || 'Error al eliminar');
    } catch (err) {
      alert(err.response?.data?.message || 'Error de conexión');
    }
  };

  const resetForm = () => {
    setNewUser({ username: '', password: '', allowedPages: [] });
    setAcademyName('');
    setAcademyLogo('');
    setEditingUserId(null);
  };

  return (
    <SharedCard title="Gestión de Usuarios">

      {/* ── Formulario ── */}
      <form onSubmit={handleAddOrUpdateUser} className="mb-10">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          {editingUserId ? '✏️ Editar Usuario' : '➕ Crear Nuevo Usuario'}
        </h3>

        {error   && <div className="mb-4 p-3 bg-red-50   text-red-700   border border-red-200   rounded-lg text-sm">{error}</div>}
        {success && <div className="mb-4 p-3 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm">{success}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Usuario*</label>
            <input
              name="username"
              value={newUser.username}
              onChange={handleInputChange}
              disabled={!!editingUserId}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#961914]/30 disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">
              Contraseña* {editingUserId && <span className="text-gray-400 font-normal">(dejar vacío para no cambiar)</span>}
            </label>
            <input
              name="password"
              type="password"
              value={newUser.password}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#961914]/30"
            />
          </div>
        </div>

        {/* Páginas permitidas */}
        <div className="mb-4">
          <label className="block text-sm font-bold text-gray-700 mb-2">Páginas Permitidas</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
            {availablePages.map(page => (
              <label key={page.value} className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  value={page.value}
                  checked={newUser.allowedPages.includes(page.value)}
                  onChange={handlePageCheckboxChange}
                  className="w-4 h-4 accent-[#961914]"
                />
                <span className="text-sm text-gray-700">{page.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Configuración de academia */}
        <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-sm font-bold text-gray-700 mb-3">Configuración de la Academia</h4>
          <SharedInput
            label="Nombre de la Academia"
            type="text"
            value={academyName}
            onChange={e => setAcademyName(e.target.value)}
          />
          <ImageUploader
            label="Logo de la Academia"
            imageUrl={academyLogo}
            onImageUrlChange={setAcademyLogo}
            onFileChange={setAcademyLogo}
          />
        </div>

        <div className="flex gap-3">
          <SharedButton type="submit">
            {editingUserId ? 'Actualizar Usuario' : 'Crear Usuario'}
          </SharedButton>
          {editingUserId && (
            <SharedButton onClick={resetForm} className="bg-gray-500 hover:bg-gray-600">
              Cancelar
            </SharedButton>
          )}
        </div>
      </form>

      {/* ── Tabla de usuarios ── */}
      <h3 className="text-lg font-bold text-gray-900 mb-4">
        Usuarios registrados ({users.length})
      </h3>

      {users.length === 0 ? (
        <p className="text-gray-400 text-center py-8">No hay usuarios registrados.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-[#961914] text-white text-xs uppercase tracking-wider">
                <th className="py-3 px-5 text-left">Usuario</th>
                <th className="py-3 px-5 text-left">Academia</th>
                <th className="py-3 px-5 text-left">Páginas</th>
                <th className="py-3 px-5 text-left">Creado</th>
                <th className="py-3 px-5 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(u => (
                <tr key={u.id} className={`hover:bg-gray-50 transition-colors ${editingUserId === u.id ? 'bg-[#961914]/5' : ''}`}>
                  <td className="py-3 px-5">
                    <div className="flex items-center gap-2">
                      {u.academy_logo && (
                        <img src={u.academy_logo} alt="" className="w-7 h-7 rounded-full object-cover border border-gray-200" />
                      )}
                      <span className="font-semibold text-sm text-gray-900">{u.username}</span>
                      {u.username === 'admin' && (
                        <span className="text-xs bg-[#961914] text-white px-2 py-0.5 rounded-full">super</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-5 text-sm text-gray-600">{u.academy_name || '—'}</td>
                  <td className="py-3 px-5 text-xs text-gray-500">
                    {u.username === 'admin'
                      ? <span className="text-[#961914] font-semibold">Acceso total</span>
                      : (u.pages || [])
                          .map(code => availablePages.find(ap => ap.value === code)?.label)
                          .filter(Boolean)
                          .join(', ') || '—'
                    }
                  </td>
                  <td className="py-3 px-5 text-xs text-gray-400">
                    {new Date(u.created_at).toLocaleDateString('es-MX')}
                  </td>
                  <td className="py-3 px-5 text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => handleEditUser(u)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                      >
                        Editar
                      </button>
                      {u.username !== 'admin' && (
                        <button
                          onClick={() => handleDeleteUser(u.id)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SharedCard>
  );
};

export default UserManagement;